import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { MultiSelect } from '@/components/ui/MultiSelect';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import {
  useEmailTemplatesList,
  getTemplateTypeLabel,
  getTemplateCategory,
} from '@/features/settings/hooks/useEmailTemplates';
import {
  useAutomationRules,
  useAutomationMetadata,
  useAutomationFields,
  useAutomationRuleLog,
  useCreateAutomationRule,
  useUpdateAutomationRule,
  useToggleAutomationRule,
  useDeleteAutomationRule,
  useTestAutomationRule,
} from './hooks/useAutomationRules';
import type {
  AutomationRule,
  AutomationRuleRequest,
  AutomationMetadata,
  AutomationEntity,
  TriggerType,
  ConditionType,
  ActionType,
  ConditionOperator,
  DateOffsetDirection,
  TriggerConfig,
  ConditionConfig,
  ConditionClause,
  ActionConfig,
  ExecutionLogEntry,
} from './hooks/useAutomationRules';

// ─── Static fallback option maps (used for labels/badges) ────────────────────

const TRIGGER_TYPE_OPTIONS = [
  { value: 'date_trigger', label: 'Date / deadline' },
  { value: 'status_change', label: 'Status change' },
  { value: 'task_completion', label: 'Task completed' },
  { value: 'condition_met', label: 'Condition met' },
];

const ACTION_TYPE_OPTIONS = [
  { value: 'notify', label: 'Send a notification' },
  { value: 'assign', label: 'Assign to a user' },
  { value: 'status_change', label: 'Change status' },
  { value: 'create_task', label: 'Create a task' },
];

/**
 * Maps an automation entity type to the email-template categories relevant to
 * it (categories come from getTemplateCategory). The notify dropdown shows only
 * templates in these categories plus the entity-agnostic 'automation' ones.
 * Entities without an entry fall back to automation-only templates.
 */
const ENTITY_TEMPLATE_CATEGORIES: Record<string, string[]> = {
  job: ['job'],
  task: ['job'],
  document_request: ['document'],
  invoice: ['billing'],
  quote: ['billing'],
  esignature: ['document'],
  contact: ['contact', 'portal'],
};

const TRIGGER_BADGE_MAP: Record<string, 'info' | 'warning' | 'success' | 'default'> = {
  status_change: 'info',
  task_completion: 'success',
  date_trigger: 'warning',
  condition_met: 'default',
};

const ACTION_BADGE_MAP: Record<string, 'info' | 'warning' | 'success' | 'danger'> = {
  assign: 'info',
  status_change: 'warning',
  notify: 'success',
  create_task: 'default' as 'info',
};

// ─── Plain-language rule summary ─────────────────────────────────────────────

/**
 * Renders a human-readable one-line summary of a rule, e.g.
 * "When a Document request's Due date is 5 days after → Send a notification".
 */
function summarizeRule(rule: AutomationRule, meta?: AutomationMetadata): string {
  const entity =
    meta?.entities.find((e) => e.value === rule.triggerConfig.entityType)?.label ||
    rule.triggerConfig.entityType ||
    'a record';

  let when = '';
  if (rule.triggerType === 'date_trigger') {
    const dateLabel =
      meta?.entities
        .find((e) => e.value === rule.triggerConfig.entityType)
        ?.dateFields.find((f) => f.value === rule.triggerConfig.dateField)?.label ||
      rule.triggerConfig.dateField ||
      'date';
    const days = rule.triggerConfig.offsetDays ?? 0;
    const dir = rule.triggerConfig.offsetDirection ?? 'on';
    when =
      dir === 'on'
        ? `${entity}'s ${dateLabel} is reached`
        : `${entity}'s ${dateLabel} is ${days} day${days === 1 ? '' : 's'} ${dir}`;
  } else if (rule.triggerType === 'status_change') {
    when = `${entity}'s status changes${rule.triggerConfig.toValue ? ` to ${rule.triggerConfig.toValue}` : ''}`;
  } else if (rule.triggerType === 'task_completion') {
    when = `a task on ${entity} is completed`;
  } else {
    when = `${entity} meets a condition`;
  }

  const action =
    ACTION_TYPE_OPTIONS.find((a) => a.value === rule.actionType)?.label || rule.actionType;

  return `When ${when} → ${action}`;
}

// ─── Main Component ─────────────────────────────────────────────────────────

/**
 * AutomationRuleDesigner — automation rules management page with a guided,
 * plain-language rule builder.
 * Requirements: 12.31, 12.32
 */
export function AutomationRuleDesigner() {
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { data: rules, isLoading, isError } = useAutomationRules();
  const { data: metadata } = useAutomationMetadata();
  const toggleMutation = useToggleAutomationRule();
  const deleteMutation = useDeleteAutomationRule();

  const selectedRule = useMemo(
    () => rules?.find((r) => r.id === selectedRuleId) ?? null,
    [rules, selectedRuleId],
  );

  function handleCreateNew() {
    setSelectedRuleId(null);
    setShowForm(true);
    setShowLog(false);
  }

  function handleEditRule(rule: AutomationRule) {
    setSelectedRuleId(rule.id);
    setShowForm(true);
    setShowLog(false);
  }

  function handleViewLog(ruleId: string) {
    setSelectedRuleId(ruleId);
    setShowLog(true);
    setShowForm(false);
  }

  function handleDeleteConfirm() {
    if (deleteConfirmId) {
      deleteMutation.mutate(deleteConfirmId);
      setDeleteConfirmId(null);
      if (selectedRuleId === deleteConfirmId) {
        setSelectedRuleId(null);
        setShowForm(false);
        setShowLog(false);
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Automations</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Set up rules that watch your records and act automatically — like alerting someone when
            a deadline passes.
          </p>
        </div>
        <Button onClick={handleCreateNew}>New automation</Button>
      </div>

      {/* Rules List */}
      {isLoading ? (
        <Card padding="md">
          <CardContent>
            <p className="py-8 text-center text-gray-500 dark:text-gray-400">
              Loading automations…
            </p>
          </CardContent>
        </Card>
      ) : isError ? (
        <Card padding="md">
          <CardContent>
            <p className="py-8 text-center text-danger-500" role="alert">
              Failed to load automation rules.
            </p>
          </CardContent>
        </Card>
      ) : !rules || rules.length === 0 ? (
        <Card padding="md">
          <CardContent>
            <div className="py-12 text-center">
              <p className="text-gray-700 dark:text-gray-200">No automations yet.</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
                Create your first automation to save time — for example, remind a client 5 days
                after a document request is overdue.
              </p>
              <Button className="mt-4" onClick={handleCreateNew}>
                Create your first automation
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border dark:border-border-dark">
                  <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                    Automation
                  </th>
                  <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Type</th>
                  <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Action</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                    Times run
                  </th>
                  <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Status</th>
                  <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400" />
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr
                    key={rule.id}
                    className="border-b border-border/50 hover:bg-gray-50 dark:border-border-dark/50 dark:hover:bg-gray-800/50"
                  >
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleEditRule(rule)}
                        className="font-medium text-brand-600 hover:underline dark:text-brand-400"
                      >
                        {rule.name}
                      </button>
                      <p className="mt-0.5 max-w-md truncate text-xs text-gray-500 dark:text-gray-400">
                        {summarizeRule(rule, metadata)}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={TRIGGER_BADGE_MAP[rule.triggerType] || 'default'} size="sm">
                        {TRIGGER_TYPE_OPTIONS.find((o) => o.value === rule.triggerType)?.label ||
                          rule.triggerType}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={ACTION_BADGE_MAP[rule.actionType] || 'default'} size="sm">
                        {ACTION_TYPE_OPTIONS.find((o) => o.value === rule.actionType)?.label ||
                          rule.actionType}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                      {rule.executionCount}
                    </td>
                    <td className="px-4 py-3">
                      <label
                        htmlFor={`rule-toggle-${rule.id}`}
                        className="inline-flex cursor-pointer items-center gap-2"
                      >
                        <input
                          id={`rule-toggle-${rule.id}`}
                          type="checkbox"
                          role="switch"
                          aria-label={`Toggle rule ${rule.name}`}
                          checked={rule.isActive}
                          disabled={toggleMutation.isPending}
                          onChange={(e) =>
                            toggleMutation.mutate({ id: rule.id, isActive: e.target.checked })
                          }
                          className="peer sr-only"
                        />
                        <span className="relative h-5 w-9 rounded-full bg-gray-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-transform peer-checked:bg-green-500 peer-checked:after:translate-x-4 dark:bg-gray-600 dark:peer-checked:bg-green-500" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {rule.isActive ? 'On' : 'Off'}
                        </span>
                      </label>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleViewLog(rule.id)}>
                          History
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirmId(rule.id)}
                        >
                          <span className="text-red-600 dark:text-red-400">Delete</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Execution Log */}
      {showLog && selectedRuleId && (
        <ExecutionLogSection
          ruleId={selectedRuleId}
          ruleName={selectedRule?.name || ''}
          onClose={() => setShowLog(false)}
        />
      )}

      {/* Rule Builder Wizard */}
      {showForm && (
        <RuleWizardModal
          rule={selectedRule ?? undefined}
          metadata={metadata}
          onClose={() => setShowForm(false)}
        />
      )}

      {/* Delete Confirmation */}
      <Modal
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete automation"
        description="This can't be undone. The automation and its history will be permanently removed."
        size="sm"
      >
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleDeleteConfirm}
            loading={deleteMutation.isPending}
          >
            <span className="text-red-600">Delete</span>
          </Button>
        </div>
      </Modal>
    </div>
  );
}

// ─── Wizard ───────────────────────────────────────────────────────────────

const STEPS = ['Basics', 'When', 'Only if', 'Then', 'Review'] as const;
type StepIndex = 0 | 1 | 2 | 3 | 4;

interface WizardState {
  name: string;
  description: string;
  triggerType: TriggerType;
  triggerConfig: TriggerConfig;
  conditionType: ConditionType;
  conditionConfig: ConditionConfig;
  actionType: ActionType;
  actionConfig: ActionConfig;
}

function initialWizardState(rule?: AutomationRule): WizardState {
  return {
    name: rule?.name ?? '',
    description: rule?.description ?? '',
    triggerType: rule?.triggerType ?? 'date_trigger',
    triggerConfig: rule?.triggerConfig ?? { offsetDirection: 'after', offsetDays: 0 },
    conditionType: rule?.conditionType ?? 'always',
    conditionConfig: normalizeConditionConfig(rule?.conditionConfig),
    actionType: rule?.actionType ?? 'notify',
    actionConfig: rule?.actionConfig ?? {},
  };
}

const EMPTY_CONDITION: ConditionClause = { field: '', operator: 'equals', value: '' };

function normalizeConditionConfig(config?: ConditionConfig | null): ConditionConfig {
  const conditions = listConditions(config);
  return {
    logic: 'and',
    conditions: conditions.length > 0 ? conditions : [{ ...EMPTY_CONDITION }],
  };
}

function listConditions(config?: ConditionConfig | null): ConditionClause[] {
  if (config?.conditions && config.conditions.length > 0) return config.conditions;
  if (config?.field) {
    return [
      {
        field: config.field,
        operator: config.operator ?? 'equals',
        value: config.value,
      },
    ];
  }
  return [];
}

/**
 * The guided, stepped rule builder. Walks the user through Basics → When →
 * Only if → Then → Review in plain language, using metadata-driven pickers so
 * they never type raw field names or rule DSL strings.
 */
function RuleWizardModal({
  rule,
  metadata,
  onClose,
}: {
  rule?: AutomationRule;
  metadata?: AutomationMetadata;
  onClose: () => void;
}) {
  const isEdit = !!rule;
  const { toast } = useToast();
  const [step, setStep] = useState<StepIndex>(0);
  const [state, setState] = useState<WizardState>(() => initialWizardState(rule));
  const [error, setError] = useState('');

  const createMutation = useCreateAutomationRule();
  const updateMutation = useUpdateAutomationRule();
  const isSaving = createMutation.isPending || updateMutation.isPending;

  const set = <K extends keyof WizardState>(key: K, value: WizardState[K]) =>
    setState((s) => ({ ...s, [key]: value }));

  const setTrigger = (patch: Partial<TriggerConfig>) =>
    setState((s) => ({ ...s, triggerConfig: { ...s.triggerConfig, ...patch } }));
  const setAction = (patch: Partial<ActionConfig>) =>
    setState((s) => ({ ...s, actionConfig: { ...s.actionConfig, ...patch } }));
  const setCondition = (patch: Partial<ConditionConfig>) =>
    setState((s) => ({ ...s, conditionConfig: { ...s.conditionConfig, ...patch } }));

  const selectedEntity: AutomationEntity | undefined = metadata?.entities.find(
    (e) => e.value === state.triggerConfig.entityType,
  );

  // Per-step validation gate for the Next button.
  function stepError(current: StepIndex): string | null {
    if (current === 0 && !state.name.trim()) return 'Give your automation a name.';
    if (current === 1) {
      if (!state.triggerConfig.entityType) return 'Choose which records this watches.';
      if (state.triggerType === 'date_trigger' && !state.triggerConfig.dateField)
        return 'Choose which date to watch.';
    }
    if (current === 2 && state.conditionType !== 'always') {
      const rows = listConditions(state.conditionConfig);
      if (!rows.some((c) => c.field)) return 'Add at least one condition, or turn the filter off.';
    }
    if (current === 3) {
      if (state.actionType === 'notify') {
        const recipientCount =
          state.actionConfig.recipients?.length ?? (state.actionConfig.recipient_id ? 1 : 0);
        if (recipientCount === 0) return 'Choose at least one recipient to notify.';
        if (!state.actionConfig.template && !state.actionConfig.title)
          return 'Add a notification title or template.';
      }
      if (state.actionType === 'assign' && !state.actionConfig.value)
        return 'Choose who to assign to.';
      if (state.actionType === 'status_change' && !state.actionConfig.value)
        return 'Enter the new status value.';
      if (state.actionType === 'create_task' && !state.actionConfig.title)
        return 'Enter a task title.';
    }
    return null;
  }

  function goNext() {
    const err = stepError(step);
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setStep((s) => Math.min(4, s + 1) as StepIndex);
  }

  function goBack() {
    setError('');
    setStep((s) => Math.max(0, s - 1) as StepIndex);
  }

  function buildPayload(): AutomationRuleRequest {
    // Normalise action_config into the shape the backend expects per action.
    let actionConfig: ActionConfig = {};
    if (state.actionType === 'notify') {
      // Recipients: prefer the multi-select list; fall back to a legacy single id.
      const recipients =
        state.actionConfig.recipients && state.actionConfig.recipients.length > 0
          ? state.actionConfig.recipients
          : state.actionConfig.recipient_id
            ? [state.actionConfig.recipient_id]
            : [];
      actionConfig = {
        channel: state.actionConfig.channel ?? 'in_app',
        recipients,
        recipient_type: state.actionConfig.recipient_type ?? 'user',
        // When an email template is attached, its `type` is the notification
        // type used to render the email. Otherwise fall back to a generic type
        // that sends with the title/body below (no stored template required).
        template: state.actionConfig.template || 'automation.notification',
        title: state.actionConfig.title,
        body: state.actionConfig.body,
      };
    } else if (state.actionType === 'assign') {
      actionConfig = {
        target_field: state.actionConfig.target_field || 'assigned_accountant_id',
        value: state.actionConfig.value,
      };
    } else if (state.actionType === 'status_change') {
      actionConfig = {
        target_field: state.actionConfig.target_field || 'status',
        value: state.actionConfig.value,
      };
    } else if (state.actionType === 'create_task') {
      actionConfig = {
        title: state.actionConfig.title,
        description: state.actionConfig.description,
        value: state.actionConfig.value,
        is_mandatory: state.actionConfig.is_mandatory ?? false,
      };
    }

    return {
      name: state.name.trim(),
      description: state.description.trim() || undefined,
      triggerType: state.triggerType,
      triggerConfig: state.triggerConfig,
      conditionType: state.conditionType,
      conditionConfig:
        state.conditionType === 'always'
          ? undefined
          : {
              logic: 'and',
              conditions: listConditions(state.conditionConfig).filter((c) => c.field),
            },
      actionType: state.actionType,
      actionConfig,
      isActive: rule?.isActive ?? true,
    };
  }

  async function handleSave() {
    // Validate all gated steps before saving.
    for (const s of [0, 1, 2, 3] as StepIndex[]) {
      const err = stepError(s);
      if (err) {
        setError(err);
        setStep(s);
        return;
      }
    }

    try {
      const payload = buildPayload();
      if (isEdit) {
        await updateMutation.mutateAsync({ id: rule.id, ...payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      toast({ variant: 'success', title: isEdit ? 'Automation updated' : 'Automation created' });
      onClose();
    } catch {
      setError('Could not save. Please review your settings and try again.');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Edit: ${rule.name}` : 'New automation'}
      size="2xl"
      data-testid="automation-wizard"
    >
      {/* Stepper */}
      <ol className="mb-6 flex items-center gap-2" aria-label="Steps">
        {STEPS.map((label, i) => {
          const active = i === step;
          const done = i < step;
          return (
            <li key={label} className="flex flex-1 items-center gap-2">
              <button
                type="button"
                onClick={() => i <= step && setStep(i as StepIndex)}
                className={`flex items-center gap-2 rounded-lg px-2 py-1 text-sm ${
                  active
                    ? 'font-semibold text-brand-600 dark:text-brand-400'
                    : done
                      ? 'text-gray-600 hover:underline dark:text-gray-300'
                      : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                    active
                      ? 'bg-brand-600 text-white'
                      : done
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500 dark:bg-gray-700'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                {label}
              </button>
              {i < STEPS.length - 1 && (
                <span className="hidden h-px flex-1 bg-border dark:bg-border-dark sm:block" />
              )}
            </li>
          );
        })}
      </ol>

      <div className="min-h-[18rem]" role="group" aria-label="Rule designer form">
        {step === 0 && (
          <BasicsStep
            name={state.name}
            description={state.description}
            onName={(v) => set('name', v)}
            onDescription={(v) => set('description', v)}
          />
        )}
        {step === 1 && (
          <WhenStep
            metadata={metadata}
            triggerType={state.triggerType}
            triggerConfig={state.triggerConfig}
            selectedEntity={selectedEntity}
            onTriggerType={(v) => set('triggerType', v)}
            onTrigger={setTrigger}
          />
        )}
        {step === 2 && (
          <OnlyIfStep
            metadata={metadata}
            conditionType={state.conditionType}
            conditionConfig={state.conditionConfig}
            entityType={state.triggerConfig.entityType}
            onConditionType={(v) => set('conditionType', v)}
            onCondition={setCondition}
          />
        )}
        {step === 3 && (
          <ThenStep
            metadata={metadata}
            actionType={state.actionType}
            actionConfig={state.actionConfig}
            selectedEntity={selectedEntity}
            onActionType={(v) => set('actionType', v)}
            onAction={setAction}
          />
        )}
        {step === 4 && <ReviewStep state={state} metadata={metadata} rule={rule} />}
      </div>

      {error && (
        <p className="mt-4 text-sm text-danger-500" role="alert">
          {error}
        </p>
      )}

      {/* Footer nav */}
      <div className="mt-6 flex items-center justify-between border-t border-border pt-4 dark:border-border-dark">
        <Button variant="ghost" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <div className="flex gap-3">
          {step > 0 && (
            <Button variant="outline" onClick={goBack} disabled={isSaving}>
              Back
            </Button>
          )}
          {step < 4 ? (
            <Button onClick={goNext}>Next</Button>
          ) : (
            <Button onClick={handleSave} loading={isSaving}>
              {isEdit ? 'Save changes' : 'Create automation'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── Step 1: Basics ──────────────────────────────────────────────────────────

function BasicsStep({
  name,
  description,
  onName,
  onDescription,
}: {
  name: string;
  description: string;
  onName: (v: string) => void;
  onDescription: (v: string) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Give your automation a clear name so your team knows what it does.
      </p>
      <Input
        label="Name"
        value={name}
        onChange={(e) => onName(e.target.value)}
        placeholder="e.g. Remind client 5 days after a document request is overdue"
        required
      />
      <Input
        label="Description (optional)"
        value={description}
        onChange={(e) => onDescription(e.target.value)}
        placeholder="A short note about when and why this runs"
      />
    </div>
  );
}

// ─── Step 2: When (trigger) ──────────────────────────────────────────────────

function WhenStep({
  metadata,
  triggerType,
  triggerConfig,
  selectedEntity,
  onTriggerType,
  onTrigger,
}: {
  metadata?: AutomationMetadata;
  triggerType: TriggerType;
  triggerConfig: TriggerConfig;
  selectedEntity?: AutomationEntity;
  onTriggerType: (v: TriggerType) => void;
  onTrigger: (patch: Partial<TriggerConfig>) => void;
}) {
  const triggerTypeOptions = (metadata?.triggerTypes ?? TRIGGER_TYPE_OPTIONS).map((t) => ({
    value: t.value,
    label: t.label,
  }));
  const entityOptions = [
    { value: '', label: 'Choose a record type…' },
    ...(metadata?.entities ?? []).map((e) => ({ value: e.value, label: e.label })),
  ];
  const dateFieldOptions = [
    { value: '', label: 'Choose a date…' },
    ...(selectedEntity?.dateFields ?? []).map((f) => ({ value: f.value, label: f.label })),
  ];
  const directionOptions = (
    metadata?.dateDirections ?? [
      { value: 'before', label: 'days before' },
      { value: 'on', label: 'on the date' },
      { value: 'after', label: 'days after' },
    ]
  ).map((d) => ({ value: d.value, label: d.label }));

  const triggerDescription = metadata?.triggerTypes.find(
    (t) => t.value === triggerType,
  )?.description;

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Choose what should start this automation.
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Select
          label="What kind of trigger?"
          value={triggerType}
          onChange={(e) => onTriggerType(e.target.value as TriggerType)}
          options={triggerTypeOptions}
        />
        <Select
          label="Which records?"
          value={triggerConfig.entityType ?? ''}
          onChange={(e) => onTrigger({ entityType: e.target.value, dateField: '', field: '' })}
          options={entityOptions}
        />
      </div>
      {triggerDescription && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{triggerDescription}</p>
      )}

      {triggerType === 'date_trigger' && (
        <div className="rounded-xl border border-border bg-surface-secondary p-4 dark:border-border-dark dark:bg-surface-dark-secondary">
          <p className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">Timing</p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[10rem] flex-1">
              <Select
                label="Watch this date"
                value={triggerConfig.dateField ?? ''}
                onChange={(e) => onTrigger({ dateField: e.target.value })}
                options={dateFieldOptions}
                disabled={!selectedEntity}
              />
            </div>
            <div className="w-24">
              <Input
                label="Days"
                type="number"
                min={0}
                value={String(triggerConfig.offsetDays ?? 0)}
                onChange={(e) =>
                  onTrigger({ offsetDays: Math.max(0, Number(e.target.value) || 0) })
                }
                disabled={triggerConfig.offsetDirection === 'on'}
              />
            </div>
            <div className="min-w-[9rem] flex-1">
              <Select
                label="Relative to date"
                value={triggerConfig.offsetDirection ?? 'after'}
                onChange={(e) =>
                  onTrigger({ offsetDirection: e.target.value as DateOffsetDirection })
                }
                options={directionOptions}
              />
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Runs once per record when this timing is reached. Records in a completed/closed status
            are skipped automatically.
          </p>
        </div>
      )}

      {triggerType === 'status_change' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="From status (optional)"
            value={triggerConfig.fromValue ?? ''}
            onChange={(e) => onTrigger({ fromValue: e.target.value })}
            placeholder="e.g. not_started"
          />
          <Input
            label="To status"
            value={triggerConfig.toValue ?? ''}
            onChange={(e) => onTrigger({ toValue: e.target.value })}
            placeholder="e.g. in_progress"
          />
        </div>
      )}
    </div>
  );
}

// ─── Step 3: Only if (conditions) ────────────────────────────────────────────

const OPERATOR_FALLBACK = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'greater_than', label: 'is greater than' },
  { value: 'less_than', label: 'is less than' },
  { value: 'contains', label: 'contains' },
  { value: 'in', label: 'is one of' },
];

function OnlyIfStep({
  metadata,
  conditionType,
  conditionConfig,
  entityType,
  onConditionType,
  onCondition,
}: {
  metadata?: AutomationMetadata;
  conditionType: ConditionType;
  conditionConfig: ConditionConfig;
  entityType?: string;
  onConditionType: (v: ConditionType) => void;
  onCondition: (patch: Partial<ConditionConfig>) => void;
}) {
  const { data: fields } = useAutomationFields(entityType);
  const fieldOptions = [
    { value: '', label: 'Choose a field…' },
    ...(fields ?? []).map((f) => ({ value: f.value, label: f.label })),
  ];
  const operatorOptions = (metadata?.operators ?? OPERATOR_FALLBACK).map((o) => ({
    value: o.value,
    label: o.label,
  }));

  const enabled = conditionType !== 'always';
  const rows = listConditions(conditionConfig);
  const conditions = rows.length > 0 ? rows : [{ ...EMPTY_CONDITION }];

  function setRows(next: ConditionClause[]) {
    onCondition({ logic: 'and', conditions: next });
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Add an optional filter so this only runs for certain records. Leave it off to always run.
        Multiple conditions all have to match (AND).
      </p>

      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          role="switch"
          checked={enabled}
          onChange={(e) => {
            if (e.target.checked) {
              onConditionType('if_then');
              setRows(conditions);
            } else {
              onConditionType('always');
            }
          }}
          className="peer sr-only"
          aria-label="Only run when a condition is met"
        />
        <span className="relative h-5 w-9 rounded-full bg-gray-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-transform peer-checked:bg-brand-500 peer-checked:after:translate-x-4 dark:bg-gray-600" />
        <span className="text-sm text-gray-700 dark:text-gray-200">
          Only run when a condition is met
        </span>
      </label>

      {enabled && (
        <div className="space-y-3">
          {conditions.map((c, index) => (
            <div key={index}>
              {index > 0 && (
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  AND
                </p>
              )}
              <div className="grid grid-cols-1 gap-3 rounded-xl border border-border p-4 dark:border-border-dark sm:grid-cols-4">
                <Select
                  label="Field"
                  value={c.field ?? ''}
                  onChange={(e) =>
                    setRows(
                      conditions.map((row, i) =>
                        i === index ? { ...row, field: e.target.value } : row,
                      ),
                    )
                  }
                  options={fieldOptions}
                  disabled={!entityType}
                />
                <Select
                  label="Is"
                  value={c.operator ?? 'equals'}
                  onChange={(e) =>
                    setRows(
                      conditions.map((row, i) =>
                        i === index
                          ? { ...row, operator: e.target.value as ConditionOperator }
                          : row,
                      ),
                    )
                  }
                  options={operatorOptions}
                />
                <Input
                  label="Value"
                  value={Array.isArray(c.value) ? c.value.join(', ') : (c.value ?? '')}
                  onChange={(e) =>
                    setRows(
                      conditions.map((row, i) =>
                        i === index ? { ...row, value: e.target.value } : row,
                      ),
                    )
                  }
                  placeholder="e.g. high"
                />
                <div className="flex items-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={conditions.length === 1}
                    onClick={() => setRows(conditions.filter((_, i) => i !== index))}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRows([...conditions, { ...EMPTY_CONDITION }])}
          >
            Add condition
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Step 4: Then (action) ───────────────────────────────────────────────────

function ThenStep({
  metadata,
  actionType,
  actionConfig,
  selectedEntity,
  onActionType,
  onAction,
}: {
  metadata?: AutomationMetadata;
  actionType: ActionType;
  actionConfig: ActionConfig;
  selectedEntity?: AutomationEntity;
  onActionType: (v: ActionType) => void;
  onAction: (patch: Partial<ActionConfig>) => void;
}) {
  const actionOptions = (metadata?.actionTypes ?? ACTION_TYPE_OPTIONS).map((a) => ({
    value: a.value,
    label: a.label,
  }));
  const actionDescription = metadata?.actionTypes.find((a) => a.value === actionType)?.description;

  // Email templates the user can attach to a notify action. Only meaningful for
  // the email channel; the selected template's `type` is stored in
  // actionConfig.template and used to render the email subject/body.
  const channelValue = actionConfig.channel ?? 'in_app';
  const isEmail = channelValue === 'email' || channelValue === 'both';
  const { data: emailTemplates } = useEmailTemplatesList(
    isEmail ? { channel: 'email', is_active: true } : undefined,
  );

  // Show only templates relevant to the chosen record type: the entity's
  // category(ies) plus the entity-agnostic 'automation.*' templates. If the
  // rule's currently-saved template falls outside that set (e.g. edited later),
  // keep it so it isn't silently dropped.
  const allowedCategories = new Set([
    'automation',
    ...(ENTITY_TEMPLATE_CATEGORIES[selectedEntity?.value ?? ''] ?? []),
  ]);
  const relevantTemplates = (emailTemplates ?? []).filter(
    (t) => allowedCategories.has(getTemplateCategory(t.type)) || t.type === actionConfig.template,
  );
  const templateOptions = [
    { value: '', label: 'No template — use the title/body below' },
    ...relevantTemplates.map((t) => ({
      value: t.type,
      label: getTemplateTypeLabel(t.type),
    })),
  ];
  const hasTemplate = Boolean(actionConfig.template);

  // Selected recipients — prefer the new recipients[] list, falling back to a
  // legacy single recipient_id so older rules still show their selection.
  const selectedRecipients =
    actionConfig.recipients ?? (actionConfig.recipient_id ? [actionConfig.recipient_id] : []);

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Choose what happens when the automation fires.
      </p>

      <Select
        label="Do this"
        value={actionType}
        onChange={(e) => onActionType(e.target.value as ActionType)}
        options={actionOptions}
      />
      {actionDescription && (
        <p className="-mt-2 text-xs text-gray-500 dark:text-gray-400">{actionDescription}</p>
      )}

      {actionType === 'notify' && (
        <div className="space-y-3 rounded-xl border border-border p-4 dark:border-border-dark">
          {/* Recipients + channel side by side. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <MultiSelect
              label="Notify"
              options={(selectedEntity?.recipients ?? []).map((r) => ({
                value: r.value,
                label: r.label,
              }))}
              value={selectedRecipients}
              onChange={(next) => onAction({ recipients: next, recipient_id: undefined })}
              disabled={(selectedEntity?.recipients ?? []).length === 0}
              placeholder={
                (selectedEntity?.recipients ?? []).length === 0
                  ? 'Choose a record type first…'
                  : 'Select recipients…'
              }
              data-testid="notify-recipients"
            />
            <Select
              label="How"
              value={channelValue}
              onChange={(e) => {
                const channel = e.target.value as 'email' | 'in_app' | 'both';
                // Templates only apply to email — clear the selection when the
                // channel has no email component (in-app only) so we don't keep
                // a template that will never be used.
                const keepsEmail = channel === 'email' || channel === 'both';
                onAction(keepsEmail ? { channel } : { channel, template: '' });
              }}
              options={[
                { value: 'in_app', label: 'In-app notification' },
                { value: 'email', label: 'Email' },
                { value: 'both', label: 'Both (in-app + email)' },
              ]}
            />
          </div>
          <p className="-mt-1 text-xs text-gray-500 dark:text-gray-400">
            Select everyone who should be notified — each gets their own notification.
          </p>

          {isEmail && (
            <Select
              label="Email template"
              value={actionConfig.template ?? ''}
              onChange={(e) => onAction({ template: e.target.value })}
              options={templateOptions}
              hint="Pick a template (e.g. Escalation) or manage them in Settings → Email Templates. Merge fields like {{client.first_name}}, {{title}}, and {{due_date}} are filled from the triggering record."
            />
          )}

          <Input
            label={hasTemplate ? 'Fallback title' : 'Message title'}
            value={actionConfig.title ?? ''}
            onChange={(e) => onAction({ title: e.target.value })}
            placeholder="e.g. Document request overdue"
            hint={hasTemplate ? 'Used only if the template has no subject.' : undefined}
          />
          <Input
            label={hasTemplate ? 'Fallback body (optional)' : 'Message body (optional)'}
            value={actionConfig.body ?? ''}
            onChange={(e) => onAction({ body: e.target.value })}
            placeholder="e.g. Please upload the requested documents as soon as possible."
          />
        </div>
      )}

      {actionType === 'assign' && (
        <div className="grid grid-cols-1 gap-3 rounded-xl border border-border p-4 dark:border-border-dark sm:grid-cols-2">
          <Input
            label="Field to set"
            value={actionConfig.target_field ?? ''}
            onChange={(e) => onAction({ target_field: e.target.value })}
            placeholder="e.g. assigned_manager_id"
          />
          <Input
            label="Assign to"
            value={actionConfig.value ?? ''}
            onChange={(e) => onAction({ value: e.target.value })}
            placeholder="A user ID or rule:manager"
          />
        </div>
      )}

      {actionType === 'status_change' && (
        <div className="grid grid-cols-1 gap-3 rounded-xl border border-border p-4 dark:border-border-dark sm:grid-cols-2">
          <Input
            label="Field to change"
            value={actionConfig.target_field ?? ''}
            onChange={(e) => onAction({ target_field: e.target.value })}
            placeholder="e.g. status"
          />
          <Input
            label="New value"
            value={actionConfig.value ?? ''}
            onChange={(e) => onAction({ value: e.target.value })}
            placeholder="e.g. in_review"
          />
        </div>
      )}

      {actionType === 'create_task' && (
        <div className="space-y-3 rounded-xl border border-border p-4 dark:border-border-dark">
          <Input
            label="Task title"
            value={actionConfig.title ?? ''}
            onChange={(e) => onAction({ title: e.target.value })}
            placeholder="e.g. Follow up with client"
          />
          <Input
            label="Assignee (optional user ID)"
            value={actionConfig.value ?? ''}
            onChange={(e) => onAction({ value: e.target.value })}
            placeholder="Optional user ID"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Task creation currently applies to Job records.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Step 5: Review ──────────────────────────────────────────────────────────

function ReviewStep({
  state,
  metadata,
  rule,
}: {
  state: WizardState;
  metadata?: AutomationMetadata;
  rule?: AutomationRule;
}) {
  const testMutation = useTestAutomationRule();

  // Reconstruct a rule-like object purely for the summary line.
  const previewRule = {
    name: state.name,
    triggerType: state.triggerType,
    triggerConfig: state.triggerConfig,
    actionType: state.actionType,
  } as AutomationRule;

  const entityLabel =
    metadata?.entities.find((e) => e.value === state.triggerConfig.entityType)?.label ??
    state.triggerConfig.entityType ??
    '—';

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-brand-200 bg-brand-50 p-4 dark:border-brand-900 dark:bg-brand-950/40">
        <p className="text-sm font-medium text-brand-800 dark:text-brand-200">In plain English</p>
        <p className="mt-1 text-sm text-gray-800 dark:text-gray-100">
          {summarizeRule(previewRule, metadata)}
        </p>
      </div>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <ReviewRow label="Name" value={state.name || '—'} />
        <ReviewRow label="Records" value={entityLabel} />
        <ReviewRow
          label="Trigger"
          value={
            TRIGGER_TYPE_OPTIONS.find((t) => t.value === state.triggerType)?.label ??
            state.triggerType
          }
        />
        <ReviewRow
          label="Action"
          value={
            ACTION_TYPE_OPTIONS.find((a) => a.value === state.actionType)?.label ?? state.actionType
          }
        />
        <ReviewRow
          label="Condition"
          value={
            state.conditionType === 'always'
              ? 'Always run'
              : `All ${listConditions(state.conditionConfig).filter((c) => c.field).length || 1} conditions must match (AND)`
          }
        />
      </dl>

      {rule && (
        <div className="rounded-xl border border-border p-4 dark:border-border-dark">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              Test this automation
            </p>
            <Button
              variant="outline"
              size="sm"
              loading={testMutation.isPending}
              onClick={() =>
                testMutation.mutate({
                  ruleId: rule.id,
                  entityType: state.triggerConfig.entityType,
                })
              }
            >
              Run dry run
            </Button>
          </div>
          {testMutation.data && (
            <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">
              <p>
                {testMutation.data.wouldTrigger
                  ? `Would match ${testMutation.data.matchedEntities} record(s).`
                  : 'No records currently match.'}
              </p>
              {testMutation.data.errors.length > 0 && (
                <p className="mt-1 text-danger-500">{testMutation.data.errors.join('; ')}</p>
              )}
            </div>
          )}
          {testMutation.isError && (
            <p className="mt-3 text-sm text-danger-500">Dry run failed. Please try again.</p>
          )}
        </div>
      )}
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/50 py-1 dark:border-border-dark/50">
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-right font-medium text-gray-800 dark:text-gray-100">{value}</dd>
    </div>
  );
}

// ─── Execution Log Section ──────────────────────────────────────────────────

function resultPreview(result: Record<string, unknown> | null): string {
  if (!result) return '—';
  if (result.error) return String(result.error);
  if (result.action_details) return JSON.stringify(result.action_details);
  return JSON.stringify(result);
}

function ExecutionLogSection({
  ruleId,
  ruleName,
  onClose,
}: {
  ruleId: string;
  ruleName: string;
  onClose: () => void;
}) {
  const { data: entries, isLoading } = useAutomationRuleLog(ruleId);
  const [detail, setDetail] = useState<ExecutionLogEntry | null>(null);
  const detailResult = detail?.actionResult ?? null;

  return (
    <>
      <Modal open onClose={onClose} title={`History: ${ruleName}`} size="2xl">
        <div>
          {isLoading ? (
            <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
              Loading history…
            </p>
          ) : !entries || entries.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
              This automation hasn't run yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-border dark:border-border-dark">
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Time</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Record</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Action</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry: ExecutionLogEntry) => {
                    const result = entry.actionResult as Record<string, unknown> | null;
                    const success = result?.success === true;
                    const preview = resultPreview(result);
                    return (
                      <tr
                        key={entry.id}
                        className="border-b border-border/50 dark:border-border-dark/50"
                      >
                        <td className="py-2 text-gray-700 dark:text-gray-300">
                          {new Date(entry.executedAt).toLocaleString()}
                        </td>
                        <td className="py-2 text-gray-700 dark:text-gray-300">
                          <span className="capitalize">{entry.triggerEntityType}</span>
                          <span className="ml-1 text-xs text-gray-400">
                            {(entry.triggerEntityId || '—').toString().slice(0, 8)}
                            {entry.triggerEntityId ? '…' : ''}
                          </span>
                        </td>
                        <td className="py-2">
                          <Badge variant={success ? 'success' : 'danger'} size="sm">
                            {entry.actionTaken}
                          </Badge>
                        </td>
                        <td className="max-w-[200px] py-2 text-xs text-gray-600 dark:text-gray-400">
                          <button
                            type="button"
                            className="block w-full truncate text-left text-brand-600 underline-offset-2 hover:underline dark:text-brand-400"
                            onClick={() => setDetail(entry)}
                            aria-label="View result details"
                            title="View full result"
                          >
                            {preview.length > 60 ? `${preview.slice(0, 60)}…` : preview}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
      {createPortal(
        <Modal open={detail !== null} onClose={() => setDetail(null)} title="Run details" size="lg">
          {detail && (
            <div className="space-y-3 text-sm">
              <dl className="space-y-2">
                <ReviewRow label="Time" value={new Date(detail.executedAt).toLocaleString()} />
                <ReviewRow
                  label="Record"
                  value={`${detail.triggerEntityType} ${detail.triggerEntityId || '—'}`}
                />
                <ReviewRow label="Action" value={detail.actionTaken} />
                <ReviewRow
                  label="Outcome"
                  value={
                    detailResult?.success === true
                      ? 'Succeeded'
                      : detailResult?.error
                        ? String(detailResult.error)
                        : '—'
                  }
                />
              </dl>
              <div>
                <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                  Full result
                </p>
                <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap break-all rounded-md bg-gray-50 p-3 font-mono text-xs text-gray-800 dark:bg-gray-900 dark:text-gray-100">
                  {detailResult ? JSON.stringify(detailResult, null, 2) : '—'}
                </pre>
              </div>
            </div>
          )}
        </Modal>,
        document.body,
      )}
    </>
  );
}
